import { useState, useEffect, useRef, useCallback } from 'react';

const cache = new Map();
const CACHE_TTL = 15000;

function getCacheKey(fn, params) {
  return `${fn.name}:${JSON.stringify(params)}`;
}

export function useApi(apiFunction, params = null, autoFetch = true) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const paramsRef = useRef(params);

  const fetchData = useCallback(async (overrideParams) => {
    const currentParams = overrideParams !== undefined ? overrideParams : paramsRef.current;

    if (abortRef.current) {
      abortRef.current.abort();
    }
    const abortController = new AbortController();
    abortRef.current = abortController;

    const cacheKey = getCacheKey(apiFunction, currentParams);
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setData(cached.data);
      setLoading(false);
      setError(null);
      return cached.data;
    }

    setLoading(true);
    setError(null);

    try {
      let result;
      if (Array.isArray(currentParams)) {
        result = await apiFunction(...currentParams);
      } else if (currentParams !== null && currentParams !== undefined) {
        result = await apiFunction(currentParams);
      } else {
        result = await apiFunction();
      }

      if (abortController.signal.aborted) return;
      if (!mountedRef.current) return;

      cache.set(cacheKey, { data: result, timestamp: Date.now() });
      setData(result);
      setLoading(false);
      return result;
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (!mountedRef.current) return;
      setError(err);
      setLoading(false);
      return null;
    }
  }, [apiFunction]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    const prevParams = JSON.stringify(paramsRef.current);
    const newParams = JSON.stringify(params);

    if (prevParams !== newParams) {
      paramsRef.current = params;
    }

    if (autoFetch) {
      fetchData(params);
    }
  }, [autoFetch, fetchData, JSON.stringify(params)]);

  const refetch = useCallback((newParams) => {
    if (newParams !== undefined) {
      paramsRef.current = newParams;
    }
    return fetchData(newParams);
  }, [fetchData]);

  const invalidateCache = useCallback(() => {
    const cacheKey = getCacheKey(apiFunction, paramsRef.current);
    cache.delete(cacheKey);
  }, [apiFunction]);

  return { data, loading, error, refetch, invalidateCache };
}

export default useApi;
